import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { officerService } from '../../services/officerService';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Users,
  User,
  Phone,
  Mail,
  MapPin,
  Shield,
  Calendar,
  Eye,
  EyeOff,
  Filter
} from 'lucide-react';

const OfficerIncharge = () => {
  const [officers, setOfficers] = useState([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingOfficer, setEditingOfficer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();

  // Load officers from API only (no local fallback)
  const loadOfficers = async () => {
    try {
      const response = await officerService.getOfficers();
      if (response.success) {
        setOfficers(response.data || response);
      } else {
        throw new Error(response.message || 'Failed to load officers');
      }
    } catch (error) {
      console.warn('Failed to load officers from API:', error.message);
      setOfficers([]);
    }
  };

  useEffect(() => {
    loadOfficers();
  }, []);

  const filteredOfficers = officers.filter(officer => {
    const q = (searchTerm || '').toLowerCase();
    const haystack = [
      officer.code,
      officer.name,
      officer.username,
      officer.email,
      officer.phone,
      officer.designation,
      officer.role,
      officer.status,
      officer.type
    ].filter(Boolean).join(' ').toLowerCase();
    const matchesSearch = !q || haystack.includes(q);
    const matchesStatus = filterStatus === 'all' || officer.status === filterStatus;
    const matchesType = filterType === 'all' || officer.type === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const generateOfficerCode = () => {
    const maxCode = Math.max(
      ...officers.map(off => {
        const n = parseInt(String(off.code || '').replace(/\D/g, ''));
        return isNaN(n) ? 0 : n;
      }),
      0
    );
    return `OFF${String(maxCode + 1).padStart(3, '0')}`;
  };

  const handleAdd = async (data) => {
    setIsLoading(true);
    try {
      const officerData = {
        ...data,
        code: generateOfficerCode(),
        status: data.status || 'active',
        joinDate: new Date().toISOString().split('T')[0]
      };

      const response = await officerService.createOfficer(officerData);
      if (response.success) {
        await loadOfficers(); // Reload data from API
        toast.success('Officer added successfully!');
        setShowAddModal(false);
        reset();
        setFilterType('all');
        setFilterStatus('all');
      } else {
        throw new Error(response.message || 'Failed to add officer');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to add officer');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (data) => {
    setIsLoading(true);
    try {
      const response = await officerService.updateOfficer(editingOfficer.id, data);
      if (response.success) {
        await loadOfficers(); // Reload data from API
        toast.success('Officer updated successfully!');
        setShowAddModal(false);
        setEditingOfficer(null);
        reset();
        setFilterType('all');
        setFilterStatus('all');
      } else {
        throw new Error(response.message || 'Failed to update officer');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to update officer');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (officerId) => {
    if (!window.confirm('Are you sure you want to delete this officer?')) return;

    setIsLoading(true);
    try {
      const response = await officerService.deleteOfficer(officerId);
      if (response.success) {
        await loadOfficers(); // Reload data from API
        toast.success('Officer deleted successfully!');
        setFilterType('all');
        setFilterStatus('all');
      } else {
        throw new Error(response.message || 'Failed to delete officer');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to delete officer');
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (officer) => {
    setEditingOfficer(officer);
    setValue('name', officer.name);
    setValue('username', officer.username);
    setValue('email', officer.email);
    setValue('phone', officer.phone);
    setValue('designation', officer.designation);
    setValue('type', officer.type || '');
    setValue('status', officer.status || 'active');
    setShowAddModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'left':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'TNAU':
        return 'bg-purple-100 text-purple-800';
      case 'GoTN':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  // Hide edit/delete for protected accounts like System Administrator
  const isProtectedOfficer = (officer) => {
    const username = (officer?.username || '').toLowerCase();
    const name = (officer?.name || '').toLowerCase();
    const designation = (officer?.designation || '').toLowerCase();
    const role = (officer?.role || '').toLowerCase();

    return (
      username === 'admin' ||
      name.includes('system administrator') ||
      designation.includes('system administrator') ||
      role === 'admin' ||
      role.includes('system administrator')
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Officer Incharge Management</h1>
          <p className="text-gray-600">Manage field officers and their login credentials</p>
        </div>
        <button 
          onClick={() => {
            setEditingOfficer(null);
            reset();
            setShowAddModal(true);
            setFilterType('all');
            setFilterStatus('all');
          }}
          className="btn-primary flex items-center mt-4 sm:mt-0"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Officer
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search officers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="left">Left</option>
          </select>
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Incharge</option>
            <option value="TNAU">TNAU</option>
            <option value="GoTN">GoTN</option>
          </select>
        </div>
      </div>

      {/* Officers Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Incharge</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOfficers.map((officer) => (
                <tr key={officer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono font-semibold text-blue-600">{officer.code}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                        <User className="w-4 h-4 text-primary-600" />
                      </div>
                      <div className="text-sm font-medium text-gray-900">{officer.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">@{officer.username}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{officer.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{officer.phone}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{officer.designation}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {officer.type ? (
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(officer.type)}`}>
                        {officer.type}
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                        Not Assigned
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(officer.status)}`}>
                      {officer.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {!isProtectedOfficer(officer) && (
                        <>
                          <button onClick={() => openEditModal(officer)} className="text-blue-600 hover:text-blue-800">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(officer.id)} className="text-red-600 hover:text-red-800">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredOfficers.length === 0 && (
          <div className="text-center py-10">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No officers found.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-700">
          Showing 1 to {filteredOfficers.length} of {filteredOfficers.length} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            disabled
            className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-400 cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-3 py-1 text-sm text-gray-700">
            Page 1 of 1
          </span>
          <button
            disabled
            className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-400 cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingOfficer ? 'Edit' : 'Add'} Officer
              </h2>
              <form onSubmit={handleSubmit(editingOfficer ? handleEdit : handleAdd)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input
                    type="text"
                    value={editingOfficer ? editingOfficer.code : generateOfficerCode()}
                    className="input-field bg-gray-50 font-mono"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    {...register('name', { required: 'Name is required' })}
                    className="input-field"
                    placeholder="Enter full name"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    type="text"
                    {...register('username', { 
                      required: 'Username is required',
                      pattern: {
                        value: /^[a-zA-Z0-9._-]+$/,
                        message: 'Username can only contain letters, numbers, dots, underscores, and hyphens'
                      }
                    })}
                    className="input-field"
                    placeholder="Enter username for login"
                  />
                  {errors.username && <p className="text-red-500 text-sm mt-1">{errors.username.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...register('password', { 
                        required: editingOfficer ? false : 'Password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters'
                        }
                      })}
                      className="input-field pr-10"
                      placeholder={editingOfficer ? "Leave blank to keep current password" : "Enter password for login"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>}
                  {editingOfficer && (
                    <p className="text-xs text-gray-500 mt-1">Leave blank to keep the current password</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className="input-field"
                    placeholder="Enter email address"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    {...register('phone', { required: 'Phone is required' })}
                    className="input-field"
                    placeholder="Enter phone number"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                  <input
                    type="text"
                    {...register('designation', { required: 'Designation is required' })}
                    className="input-field"
                    placeholder="Enter designation"
                  />
                  {errors.designation && <p className="text-red-500 text-sm mt-1">{errors.designation.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Incharge</label>
                  <select
                    {...register('type', { required: 'Incharge is required' })}
                    className="input-field"
                    defaultValue=""
                  >
                    <option value="" disabled>Select Incharge</option>
                    <option value="TNAU">TNAU</option>
                    <option value="GoTN">GoTN</option>
                  </select>
                  {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    defaultValue="active"
                    {...register('status', { required: 'Status is required' })}
                    className="input-field"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingOfficer(null);
                      reset();
                      setShowPassword(false);
                      setFilterType('all');
                      setFilterStatus('all');
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? 'Saving...' : (editingOfficer ? 'Update' : 'Add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfficerIncharge; 